import { useState, useCallback, useEffect, useRef } from 'react';
import { X, FileUp, Loader2, Trash2, File } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useToastStore } from '@/stores/toast-store';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface FileUploadModalProps {
  onClose: () => void;
  onCreated: (connectionId: string) => void;
}

interface UploadedFileInfo {
  id: string;
  fileName: string;
  tableName: string;
  columns: Array<{ name: string; type: string }>;
  rowCount: number;
  format: string;
  fileSize: number;
}

// ═══════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ACCEPTED_EXTENSIONS = '.csv,.tsv,.json,.jsonl,.parquet,.xlsx,.xls,.sqlite,.db';

async function uploadFile(file: File): Promise<UploadedFileInfo> {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('dbgo_token');
  const res = await fetch('/api/files/upload', {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let errorMsg = `Upload failed (${res.status})`;
    try {
      const err = JSON.parse(text);
      if (err.error) errorMsg = err.error;
    } catch {
      if (text) errorMsg = text.slice(0, 200);
    }
    throw new Error(errorMsg);
  }

  return (await res.json()) as UploadedFileInfo;
}

// ═══════════════════════════════════════════
// CSS Classes (matching ConnectionModal style)
// ═══════════════════════════════════════════

const inputClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors';

const labelClass = 'block text-xs font-semibold text-text-secondary mb-1.5';

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function FileUploadModal({ onClose, onCreated }: FileUploadModalProps) {
  const addToast = useToastStore((s) => s.addToast);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [connectionName, setConnectionName] = useState('My Data Files');
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileInfo[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !uploading && !creating) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, uploading, creating]);

  const handleFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    setError('');

    for (const file of files) {
      try {
        const result = await uploadFile(file);
        setUploadedFiles((prev) => [...prev, result]);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : `Failed to upload ${file.name}`;
        setError(message);
        addToast('error', message);
      }
    }

    setUploading(false);
  }, [addToast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [handleFiles]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    handleFiles(files);
    // Reset so the same file can be selected again
    e.target.value = '';
  }, [handleFiles]);

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const handleCreateConnection = useCallback(async () => {
    if (!connectionName.trim() || uploadedFiles.length === 0) return;

    setCreating(true);
    setError('');
    try {
      const result = await apiClient.post<{ connectionId: string }>('/api/files/create-connection', {
        name: connectionName.trim(),
        fileIds: uploadedFiles.map((f) => f.id),
      });
      addToast('success', `Connection "${connectionName}" created with ${uploadedFiles.length} file(s)`);
      onCreated(result.connectionId);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create connection';
      setError(message);
      addToast('error', message);
    } finally {
      setCreating(false);
    }
  }, [connectionName, uploadedFiles, addToast, onCreated]);

  const canCreate = connectionName.trim().length > 0 && uploadedFiles.length > 0 && !uploading && !creating;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={uploading || creating ? undefined : onClose}
    >
      <div
        className="w-[580px] max-h-[80vh] flex flex-col bg-dbgo-modal border rounded-lg shadow-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-0">
          <h2 className="text-sm font-semibold text-text-primary">Upload Files</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={uploading || creating}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-50"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {/* Connection Name */}
          <div>
            <label htmlFor="file-conn-name" className={labelClass}>Connection Name</label>
            <input
              id="file-conn-name"
              type="text"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder="My Data Files"
              autoFocus
              disabled={creating}
              className={inputClass}
            />
          </div>

          {/* Drop Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg px-8 py-12 text-center cursor-pointer transition-colors ${
              dragActive
                ? 'border-accent-blue bg-accent-blue/5'
                : 'border-[var(--border-default)] hover:border-[var(--border-strong)]'
            }`}
          >
            <FileUp size={24} className="mx-auto mb-2 text-text-muted" />
            <p className="text-xs text-text-secondary mb-1">
              Drop files here or click to browse
            </p>
            <p className="text-[10px] text-text-muted">
              Supports: CSV, TSV, JSON, JSONL, Parquet, Excel, SQLite
            </p>
            {uploading && (
              <div className="flex items-center justify-center gap-1.5 mt-3">
                <Loader2 size={12} className="animate-spin text-accent-blue" />
                <span className="text-xs text-accent-blue">Uploading...</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_EXTENSIONS}
            className="hidden"
            onChange={handleFileInputChange}
          />

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div>
              <span className={labelClass}>Uploaded Files</span>
              <div className="border rounded-md overflow-hidden">
                {uploadedFiles.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0 text-xs"
                  >
                    <File size={14} className="text-accent-blue flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-text-primary truncate font-medium">{f.fileName}</div>
                      <div className="text-[10px] text-text-muted">
                        {f.rowCount.toLocaleString()} rows &middot; {f.columns.length} columns &middot; {formatFileSize(f.fileSize)} &middot; {f.format.toUpperCase()}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(f.id)}
                      disabled={creating}
                      className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-accent-red hover:bg-accent-red/10 transition-colors disabled:opacity-50"
                      title="Remove file"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-5 pb-2 text-xs text-accent-red">{error}</div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading || creating}
            className="px-3 py-[6px] text-xs font-medium text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateConnection}
            disabled={!canCreate}
            className="flex items-center gap-1.5 px-4 py-[6px] text-xs font-medium text-white bg-accent-blue hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
          >
            {creating && <Loader2 size={12} className="animate-spin" />}
            {creating ? 'Creating...' : 'Create Connection'}
          </button>
        </div>
      </div>
    </div>
  );
}
